import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Button, Space, Modal, message, Form, Descriptions, Upload, Tooltip, DatePicker } from 'antd';
import { studentService } from '../../services/studentService';
import StudentForm from './components/StudentForm';
import StudentDetail from './components/StudentDetail';
import moment from 'moment';
import { UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import StudentModifyForm from './components/StudentModifyForm';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// Add getBase64 helper function
const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// Function to truncate text with ellipsis
const truncateText = (text, length = 10) => {
  if (!text) return '';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

const Students = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [modalType, setModalType] = useState('new'); // 'new', 'edit', or 'detail'
  const [searchParams, setSearchParams] = useState({
    name: '',
    uniqueId: '',
    fileId: '',
    phone: '',
    startDate: '',
    endDate: ''
  });
  const [searchForm] = Form.useForm();
  const [studentForm] = Form.useForm(); // Single form instance for the combined form
  const [withdrawReceipt, setWithdrawReceipt] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Move fetchStudents definition before useEffect
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await studentService.getStudents(searchParams);
      setStudents(data.map(student => ({
        key: student.id,
        ...student.attributes
      })));
    } catch (error) {
      message.error('获取学员数据失败');
    }
    setLoading(false);
  }, [searchParams]);

  // Now we can use fetchStudents in useEffect
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Handler for showing student details
  const showStudentDetail = (student) => {
    setCurrentStudent(student);
    setModalType('detail');
    setIsModalVisible(true);
  };

  // Handler for editing student
  const handleEdit = (student) => {
    // Convert date strings to Moment objects
    const formattedStudent = {
      ...student,
      signUpDate: student.signUpDate ? moment(student.signUpDate) : null,
      exam1Date: student.exam1Date ? moment(student.exam1Date) : null,
      exam2Date: student.exam2Date ? moment(student.exam2Date) : null,
      exam3Date: student.exam3Date ? moment(student.exam3Date) : null,
      exam4Date: student.exam4Date ? moment(student.exam4Date) : null,
    };
    
    setCurrentStudent(formattedStudent);
    setModalType('edit');
    setIsModalVisible(true);
  };

  // Handler for withdrawing student
  const handleWithdraw = (student) => {
    Modal.confirm({
      title: '确认退证',
      content: (
        <div>
          <p>确定要为以下学员办理退证吗？</p>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="姓名">{student.name}</Descriptions.Item>
            <Descriptions.Item label="身份证号">{student.uniqueId}</Descriptions.Item>
            <Descriptions.Item label="档案号">{student.fileId || '未分配'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{student.phone}</Descriptions.Item>
          </Descriptions>
          <Form layout="vertical">
            <Form.Item
              label="退证收据"
              required
              help="请上传退证收据照片"
            >
              <Upload
                accept="image/*"
                beforeUpload={(file) => {
                  setWithdrawReceipt(file);
                  return false;
                }}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>上传退证收据</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              label="退证原因"
              required
              help="请填写退证原因"
            >
              <Input.TextArea
                id="withdrawReason"
                placeholder="请输入退证原因"
              />
            </Form.Item>
          </Form>
          <p style={{ color: '#ff4d4f', marginTop: 16 }}>
            注意：退证操作不可撤销，请确认信息无误！
          </p>
        </div>
      ),
      okText: '确认退证',
      okButtonProps: {
        danger: true,
      },
      cancelText: '取消',
      width: 500,
      onOk: async () => {
        const reason = document.getElementById('withdrawReason')?.value;
        
        // Validate without throwing errors
        if (!withdrawReceipt) {
          message.error('请上传退证收据');
          return false;
        }

        if (!reason) {
          message.error('请填写退证原因');
          return false;
        }

        try {
          await studentService.withdrawStudent(student.key, {
            withdrawReceipt,
            reason
          });
          
          message.success('退证申请提交成功');
          fetchStudents();
          return true;
        } catch (error) {
          message.error('退证申请失败: ' + error.message);
          return false;
        }
      },
    });
  };

  // Simplified handleSubmit for the combined form
  const handleSubmit = async (values) => {
    try {
      if (modalType === 'new') {
        const newStudentData = {
          ...values,
          status: '正常',
          verified: false,
          documented: false,
          local: true,
        };

        // Convert dates
        if (newStudentData.signUpDate) {
          newStudentData.signUpDate = newStudentData.signUpDate.format('YYYY-MM-DD');
        }
        
        ['exam1Date', 'exam2Date', 'exam3Date', 'exam4Date'].forEach(examDate => {
          if (newStudentData[examDate]) {
            newStudentData[examDate] = newStudentData[examDate].format('YYYY-MM-DD');
          }
        });

        // Handle file uploads
        if (values.receiptPic?.originFileObj) {
          const base64Receipt = await getBase64(values.receiptPic.originFileObj);
          newStudentData.receiptPic = base64Receipt;
        }

        if (values.tuitionReceipt?.originFileObj) {
          const base64TuitionReceipt = await getBase64(values.tuitionReceipt.originFileObj);
          newStudentData.tuitionReceipt = base64TuitionReceipt;
        }

        await studentService.createStudent(newStudentData);
        message.success('添加学员成功');
      } else if (modalType === 'edit') {
        const updatedValues = { ...values };
        if (updatedValues.signUpDate) {
          updatedValues.signUpDate = updatedValues.signUpDate.format('YYYY-MM-DD');
        }
        await studentService.updateStudent(currentStudent.key, updatedValues);
        message.success('更新学员信息成功');
      }
      setIsModalVisible(false);
      fetchStudents();
    } catch (error) {
      if (error.message.includes('已存在')) {
        message.error(error.message);
      } else {
        console.error('Error:', error);
        message.error(modalType === 'new' ? '添加学员失败' : '更新学员信息失败');
      }
    }
  };

  const columns = [
    {
      title: '档案号',
      dataIndex: 'fileId',
      key: 'fileId',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        
        // 根据布尔值计算中间状态
        if (!record.verified) return '未审核';
        if (!record.documented) return '未审档';
        if (!record.interviewed) return '未面签';
        return record.status;
      }
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      render: (text, record) => (
        <Button type="link" onClick={() => showStudentDetail(record)}>
          {text}
        </Button>
      ),
    },
    {
      title: '身份证号',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
      width: 180,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: 150,
      render: (text) => (
        text ? (
          <Tooltip title={text}>
            {truncateText(text, 15)}
          </Tooltip>
        ) : '-'
      ),
    },
    {
      title: 'VIP',
      dataIndex: 'VIP',
      key: 'VIP',
      width: 60,
      render: (vip) => vip ? '是' : '否',
    },
    {
      title: '学费',
      dataIndex: 'tuitionFee',
      key: 'tuitionFee',
      width: 100,
      render: (value) => value ? `¥${value}` : '-',
    },
    {
      title: '报名日期',
      dataIndex: 'signUpDate',
      key: 'signUpDate',
      width: 120,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEdit(record)}>修改</Button>
          <Button type="link" danger onClick={() => handleWithdraw(record)}>退证</Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (values) => {
    const searchValues = { ...values };
    
    // Handle date range values if provided
    if (values.dateRange && values.dateRange.length === 2) {
      searchValues.startDate = values.dateRange[0].format('YYYY-MM-DD');
      searchValues.endDate = values.dateRange[1].format('YYYY-MM-DD');
      delete searchValues.dateRange;
    }
    
    // Filter out empty values
    const nonEmptyValues = Object.fromEntries(
      Object.entries(searchValues).filter(([_, value]) => value)
    );
    
    setSearchParams(nonEmptyValues);
  };

  // Simplified modal content renderer
  const renderModalContent = () => {
    if (modalType === 'detail') {
      return <StudentDetail student={currentStudent} />;
    }

    if (modalType === 'edit') {
      return (
        <StudentModifyForm
          onSubmit={handleSubmit}
          initialValues={currentStudent}
          form={studentForm}
        />
      );
    }

    return (
      <StudentForm
        onSubmit={handleSubmit}
        form={studentForm}
      />
    );
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isModalVisible) {
      studentForm.resetFields();
    }
  }, [isModalVisible, studentForm]);


  return (
    <Form.Provider>
      <div>
        <div style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="inline"
            onFinish={handleSearch}
          >
            <Form.Item name="name">
              <Input placeholder="姓名搜索" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="uniqueId">
              <Input placeholder="身份证搜索" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="phone">
              <Input placeholder="手机号搜索" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="fileId">
              <Input placeholder="档案号搜索" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="dateRange">
              <RangePicker 
                placeholder={['开始日期', '结束日期']} 
                style={{ width: 220 }}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  搜索
                </Button>
                <Button onClick={() => {
                  searchForm.resetFields();
                  setSearchParams({});
                  fetchStudents();
                }}>
                  重置
                </Button>
                <Tooltip title="根据报名日期筛选学员">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            </Form.Item>
          </Form>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type="primary" 
              onClick={() => setShowForm(true)}
            >
              新增学员
            </Button>
          </Space>
        </div>

        <Table 
          columns={columns} 
          dataSource={students}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1300 }}
          size="small"
        />

        <Modal
          title={modalType === 'new' ? '添加学员' : modalType === 'edit' ? '修改学员信息' : '学员详情'}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={modalType === 'detail' ? [
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              关闭
            </Button>
          ] : null}
          maskClosable={false}
          width={800}
        >
          {renderModalContent()}
        </Modal>

        <StudentForm
          open={showForm}
          onSuccess={() => {
            console.log('Form submission success, closing form');
            setShowForm(false);
            fetchStudents();
          }}
          onCancel={() => {
            console.log('Form cancel triggered, closing form');
            setShowForm(false);
          }}
        />
      </div>
    </Form.Provider>
  );
};

export default Students; 